import type { ReactNode } from 'react';
import { useState, useEffect } from 'react';
import type { BookCreateInput, Placement, ReadStatus, Author, Series, Shelf, Book } from '@/api/types';
import { PENDING_AUTHOR_ID } from '@/api/types';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { AuthorAutocomplete } from '@/components/EntityAutocomplete/AuthorAutocomplete';
import { BookTitleAutocomplete } from '@/components/BookTitleAutocomplete/BookTitleAutocomplete';
import { SeriesAutocomplete } from '@/components/EntityAutocomplete/SeriesAutocomplete';
import { ShelfAutocomplete } from '@/components/EntityAutocomplete/ShelfAutocomplete';
import { BookCover } from '@/components/BookCover/BookCover';
import styles from './BookForm.module.css';

export interface BookFormInitialValue {
  author?: Author | string;
  title?: string;
  placement?: Placement;
  series?: Series | string | null;
  shelf?: Shelf | string | null;
  readStatus?: ReadStatus | null;
  part?: number | null;
  rating?: number | null;
  comment?: string | null;
  isbn?: string | null;
  coverUrl?: string | null;
}

export interface BookFormProps {
  initialValue?: BookFormInitialValue | null;
  onSubmit: (data: BookCreateInput) => void;
  onCancel?: () => void;
  submitLabel?: string;
  loading?: boolean;
  error?: string | null;
  onDirtyChange?: (dirty: boolean) => void;
}

const PLACEMENTS: { value: Placement; label: string }[] = [
  { value: 'collection', label: 'В коллекции' },
  { value: 'wishlist', label: 'В вишлисте' },
  { value: 'in_transit', label: 'В пути' },
];

const READ_STATUSES: { value: ReadStatus | ''; label: string }[] = [
  { value: '', label: '—' },
  { value: 'not_started', label: 'Не начата' },
  { value: 'reading', label: 'Читаю' },
  { value: 'finished', label: 'Прочитана' },
];

export function BookForm({
  initialValue,
  onSubmit,
  onCancel,
  submitLabel = 'Сохранить',
  loading = false,
  error = null,
  onDirtyChange,
}: BookFormProps): ReactNode {
  const makePendingAuthor = (name: string): Author =>
    ({ id: PENDING_AUTHOR_ID, name: (name || '').trim(), createdAt: '', updatedAt: '' });
  // Нормализация начальных значений
  const getInitialAuthor = (): Author | null => {
    if (!initialValue?.author) return null;
    if (typeof initialValue.author === 'string') {
      // Устаревший формат - создание временного объекта (будет обработано автокомплитом)
      return null;
    }
    return initialValue.author;
  };

  const getInitialSeries = (): Series | null => {
    if (!initialValue?.series) return null;
    if (typeof initialValue.series === 'string') {
      return null;
    }
    return initialValue.series;
  };

  const getInitialShelf = (): Shelf | null => {
    if (!initialValue?.shelf) return null;
    if (typeof initialValue.shelf === 'string') {
      return null;
    }
    return initialValue.shelf;
  };

  const [author, setAuthor] = useState<Author | null>(() => getInitialAuthor());
  const [series, setSeries] = useState<Series | null>(() => getInitialSeries());
  const [shelf, setShelf] = useState<Shelf | null>(() => getInitialShelf());
  const [title, setTitle] = useState<string>(initialValue?.title ?? '');
  const [isbn, setIsbn] = useState<string>(initialValue?.isbn ?? '');
  const [coverUrl, setCoverUrl] = useState<string>(initialValue?.coverUrl ?? '');
  const [partValue, setPartValue] = useState<string>(
    initialValue?.part != null ? String(initialValue.part) : '',
  );
  const [seriesMaxAmountValue, setSeriesMaxAmountValue] = useState<string>(() => {
    const s = getInitialSeries();
    return s && s.maxAmount != null ? String(s.maxAmount) : '';
  });
  const [errors, setErrors] = useState<{ author?: string; title?: string }>({});
  const [isDirty, setIsDirty] = useState(false);

  const markDirty = () => {
    if (!isDirty) {
      setIsDirty(true);
      if (typeof onDirtyChange === 'function') {
        onDirtyChange(true);
      }
    }
  };

  // Обновление состояния при изменении initialValue
  useEffect(() => {
    setAuthor(getInitialAuthor());
    setSeries(getInitialSeries());
    setShelf(getInitialShelf());
    setTitle(initialValue?.title ?? '');
    setIsbn(initialValue?.isbn ?? '');
    setCoverUrl(initialValue?.coverUrl ?? '');
    setPartValue(initialValue?.part != null ? String(initialValue.part) : '');
    const nextSeries = getInitialSeries();
    setSeriesMaxAmountValue(
      nextSeries && nextSeries.maxAmount != null ? String(nextSeries.maxAmount) : '',
    );
    setErrors({});
    setIsDirty(false);
    if (typeof onDirtyChange === 'function') {
      onDirtyChange(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const trimmedTitle = title.trim();

    // Валидация
    const newErrors: { author?: string; title?: string } = {};
    if (!author) {
      newErrors.author = 'Автор обязателен для заполнения';
    } else if (author.id === PENDING_AUTHOR_ID && !author.name.trim()) {
      newErrors.author = 'Укажите имя автора';
    }
    if (!trimmedTitle) {
      newErrors.title = 'Название книги обязательно для заполнения';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    const placement = ((form.elements.namedItem('placement') as HTMLInputElement)?.value ?? 'collection') as Placement;
    const readStatusRaw = (form.elements.namedItem('readStatus') as HTMLInputElement)?.value ?? '';
    const readStatus = readStatusRaw === '' ? undefined : (readStatusRaw as ReadStatus);
    const ratingRaw = (form.elements.namedItem('rating') as HTMLInputElement)?.value ?? '';
    const rating = ratingRaw === '' ? undefined : (Number(ratingRaw) || undefined);
    const partRaw = (form.elements.namedItem('part') as HTMLInputElement)?.value ?? '';
    const part = partRaw === '' ? undefined : (Number(partRaw) || undefined);
    const seriesMaxAmountRaw = (form.elements.namedItem('seriesMaxAmount') as HTMLInputElement)
      ?.value ?? '';
    const seriesMaxAmount =
      seriesMaxAmountRaw === '' ? undefined : (Number(seriesMaxAmountRaw) || undefined);
    const comment = (form.elements.namedItem('comment') as HTMLTextAreaElement)?.value.trim() || undefined;
    const isbnValue = isbn.trim() || undefined;
    const coverUrlValue = coverUrl.trim() || undefined;

    const payload: BookCreateInput = {
      title: trimmedTitle,
      placement,
      seriesId: series?.id ?? null,
      shelfId: shelf?.id ?? null,
      readStatus: readStatus ?? null,
      part: series ? (part ?? null) : null,
      seriesMaxAmount: series ? seriesMaxAmount ?? null : null,
      rating: rating ?? null,
      comment: comment ?? null,
      isbn: isbnValue ?? null,
      coverUrl: coverUrlValue ?? null,
    };
    const authorValue = author!;
    if (authorValue.id === PENDING_AUTHOR_ID) {
      payload.author = authorValue.name.trim();
    } else {
      payload.authorId = authorValue.id;
    }
    onSubmit(payload);
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      {error && (
        <Box className={styles.error} role="alert">
          {error}
        </Box>
      )}
      <Box className={styles.layout}>
        <Box className={styles.main}>
          <Box className={styles.field}>
            <BookTitleAutocomplete
              value={title}
              onChange={(v) => {
                markDirty();
                setTitle(v);
                if (errors.title) setErrors((prev) => ({ ...prev, title: undefined }));
              }}
              onSelectLocalBook={(book: Book) => {
                markDirty();
                setTitle(book.title);
                setAuthor(book.author);
                setIsbn(book.isbn ?? '');
                setCoverUrl(book.coverUrl ?? '');
                setErrors({});
              }}
              onSelectExternalBook={(ext) => {
                markDirty();
                setTitle(ext.title);
                setAuthor(ext.author ? makePendingAuthor(ext.author) : null);
                setIsbn(ext.isbn ?? '');
                setCoverUrl(ext.coverUrl ?? '');
                setErrors({});
              }}
              disabled={loading}
              error={!!errors.title}
              helperText={errors.title}
            />
          </Box>
          <Box className={styles.field}>
            <AuthorAutocomplete
              value={author}
              onChange={(value) => {
                markDirty();
                setAuthor(value);
                if (errors.author) setErrors((prev) => ({ ...prev, author: undefined }));
              }}
              deferCreate
              disabled={loading}
              error={!!errors.author}
              helperText={errors.author}
            />
          </Box>
          <Box className={styles.row}>
            <Box className={styles.field}>
              <SeriesAutocomplete
                value={series}
                onChange={(value) => {
                  markDirty();
                  setSeries(value);
                  if (value && value.maxAmount != null) {
                    setSeriesMaxAmountValue(String(value.maxAmount));
                  } else {
                    setSeriesMaxAmountValue('');
                  }
                }}
                disabled={loading}
              />
            </Box>
            <Box className={styles.field}>
              <TextField
                fullWidth
                name="part"
                label="Часть в цикле"
                type="number"
                inputProps={{ min: 1 }}
                value={partValue}
                onChange={(e) => {
                  markDirty();
                  setPartValue(e.target.value);
                }}
                disabled={!series || loading}
                className={(!series || loading) ? styles.fieldDisabled : undefined}
              />
            </Box>
            <Box className={styles.field}>
              <TextField
                fullWidth
                name="seriesMaxAmount"
                label="Книг в цикле всего"
                type="number"
                inputProps={{ min: 1 }}
                value={seriesMaxAmountValue}
                onChange={(e) => {
                  markDirty();
                  setSeriesMaxAmountValue(e.target.value);
                }}
                disabled={!series || loading}
                className={(!series || loading) ? styles.fieldDisabled : undefined}
              />
            </Box>
          </Box>
          <Box className={styles.row}>
            <Box className={styles.field}>
              <FormControl fullWidth disabled={loading}>
                <InputLabel id="placement-label">Где находится</InputLabel>
                <Select
                  name="placement"
                  labelId="placement-label"
                  label="Где находится"
                  defaultValue={initialValue?.placement ?? 'collection'}
                  onChange={markDirty}
                >
                  {PLACEMENTS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box className={styles.field}>
              <ShelfAutocomplete
                value={shelf}
                onChange={(value) => {
                  markDirty();
                  setShelf(value);
                }}
                disabled={loading}
              />
            </Box>
          </Box>
          <Box className={styles.row}>
            <TextField
              fullWidth
              name="isbn"
              label="ISBN"
              value={isbn}
              onChange={(e) => {
                markDirty();
                setIsbn(e.target.value);
              }}
              disabled={loading}
              className={styles.field}
            />
            <Box className={styles.field}>
              <TextField
                fullWidth
                name="coverUrl"
                label="URL обложки"
                type="url"
                value={coverUrl}
                onChange={(e) => {
                  markDirty();
                  setCoverUrl(e.target.value);
                }}
                disabled={loading}
              />
            </Box>
          </Box>
          <Box className={styles.row}>
            <Box className={styles.field}>
              <FormControl fullWidth disabled={loading}>
                <InputLabel id="readStatus-label">Статус прочтения</InputLabel>
                <Select
                  name="readStatus"
                  labelId="readStatus-label"
                  label="Статус прочтения"
                  defaultValue={initialValue?.readStatus ?? ''}
                  onChange={markDirty}
                >
                  {READ_STATUSES.map((o) => (
                    <MenuItem key={o.value || 'empty'} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box className={styles.field}>
              <FormControl fullWidth disabled={loading}>
                <InputLabel id="rating-label">Рейтинг</InputLabel>
                <Select
                  name="rating"
                  labelId="rating-label"
                  label="Рейтинг"
                  defaultValue={initialValue?.rating ?? ''}
                  onChange={markDirty}
                >
                  <MenuItem value="">—</MenuItem>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <MenuItem key={n} value={n}>
                      {n} ★
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
          <TextField
            fullWidth
            name="comment"
            label="Комментарий"
            multiline
            rows={3}
            defaultValue={initialValue?.comment ?? ''}
            onChange={markDirty}
            disabled={loading}
            className={styles.field}
          />
        </Box>
        <Box className={styles.aside}>
          <Box className={styles.coverPreview}>
            <BookCover coverUrl={coverUrl} title={title} />
          </Box>
        </Box>
      </Box>
      <Box className={styles.actions}>
        {onCancel && (
          <Button
            type="button"
            variant="outlined"
            onClick={onCancel}
            disabled={loading}
            className={styles.btnSecondary}
          >
            Отмена
          </Button>
        )}
        <Button type="submit" variant="contained" disabled={loading} className={styles.btnPrimary}>
          {loading ? 'Сохранение…' : submitLabel}
        </Button>
      </Box>
    </form>
  );
}
